import { useState } from 'react';
import Box from '@mui/material/Box';
import { DataGrid } from '@mui/x-data-grid';
import { Chip, IconButton, ListItemIcon, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import { ptBR } from '../../../src/locales/ptBR';
import invoiceIcon from '../../../src/img/invoice-icon.png';
import { formatCurrency, getInvoiceStatus } from '../../helpers/financialHelpers';
import dayjs, { formatDate } from '../../helpers/dayjsConfig';
import InvoiceReceiptLongOffIcon from '../../../src/img/receipt_long_off.svg'
import CancelModal from './CancelModal';
import ErrorAlertSnackbar from '../../../components/shared/snackbars/ErrorAlertSnackbar';
import SuccessAlertSnackbar from '../../../components/shared/snackbars/SuccessAlertSnackbar';

const InvoicesTable = ({ invoices, totalCount, page, pageSize, applyFilterCallback }) => {
  const [options, setOptions] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [successSnackbarOpen, setSuccessSnackbarOpen] = useState(false);
  const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false);

  const handleClick = (event, row) => {
    setOptions(event.currentTarget);
    setSelectedRow(row);
  };

  const handleClose = () => {
    setOptions(null);
  };

  const handleCancelResult = async (isError, rowId) => {
    if (isError) {
      setErrorSnackbarOpen(true);
    } else {
      setSuccessSnackbarOpen(true);
      applyFilterCallback({ page, pageSize });
    }
  };

  const openInvoiceDetails = (invoiceId) => {
    if (invoiceId) {
      window.open(`/invoices/${invoiceId}`, "_blank");
    }
  };

  const canCancelInvoice = (status) => {
    return status === 'pending' || status === 'overdue';
  };

  const columns = [
    {
      field: 'due_date',
      headerName: 'Vencimento',
      type: 'date',
      flex: 1,
      renderCell: (params) => params?.value ? formatDate(params.value) : "-"
    },
    {
      field: 'invoice_number',
      headerName: 'Número',
      flex: 1,
    },
    {
      field: 'invoice_type',
      headerName: 'Tipo',
      flex: 1,
      renderCell: (params) => {
        const labelMap = {
          has_work: 'Vinculada',
          without_work: 'Avulsa',
        };
        const value = params.value?.toLowerCase() || '';
        return labelMap[value] || params.value;
      },
    },
    {
      field: 'customerName',
      headerName: 'Cliente',
      flex: 1,
    },
    {
      field: 'total_value',
      headerName: 'Total Cobrado',
      flex: 1,
    },
    {
      field: 'total_paid_value',
      headerName: 'Total Recebido',
      flex: 1,
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      renderCell: (params) => {
        const info = getInvoiceStatus(params.value);
        return (
          <Tooltip title={info.description || ''} arrow placement='right'>
            <Chip label={info.label} color={info.color} size="medium"/>
          </Tooltip>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Ações',
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton
          aria-label="more"
          id="long-button"
          aria-controls={open ? 'long-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-haspopup="true"
          onClick={(e) => handleClick(e, params.row)}
        >
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ];

  const rows = invoices?.map((invoice) => ({
    id: invoice.id,
    due_date: invoice.due_date ? dayjs(invoice.due_date).toDate() : null,
    invoice_number: invoice.invoice_number,
    invoice_type: invoice.invoice_type,
    customerName: invoice.customerName,
    total_value: `R$ ${formatCurrency(invoice.total_value)}`,
    total_paid_value: `R$ ${formatCurrency(invoice.total_paid_value)}`,
    status: invoice.status,
  })) || [];

  if (invoices.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
        <img src={invoiceIcon} alt="Ícone de faturas" style={{ width: '15rem' }} />
        <Typography variant="h5">Não há faturas para exibir</Typography>
      </Box>
    );
  }
  return (
    <>
      <Typography variant="h5" sx={(theme) => ({ color: theme.palette.primary.main })}>
        Faturas geradas
      </Typography>
      <Box sx={{ height: '100%', width: '100%', m: '1rem 0' }}>
        <DataGrid
          sx={{
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: 'background.header',
              padding: '0 1rem',
            },
            '& .MuiDataGrid-cell': {
              borderRight: 'none',
            },
            '& .MuiDataGrid-columnSeparator': {
              display: 'none',
            },
            '& .MuiDataGrid-row': {
              borderBottom: '1px solid theme.palette.divider',
            },
            border: 'none',
            '& .MuiDataGrid-footerContainer': {
              padding: '0.5rem',
            },
            '& .MuiDataGrid-row.Mui-selected': {
              backgroundColor: 'transparent !important',
            },
            '& .MuiDataGrid-row.Mui-selected:hover': {
              backgroundColor: 'rgba(0,0,0,0.04) !important',
            },
          }}
          rows={rows}
          columns={columns}
          pagination
          paginationMode="server"
          paginationModel={{ page: page - 1, pageSize }}
          onPaginationModelChange={(model) => applyFilterCallback({ page: model.page + 1, pageSize: model.pageSize })}
          pageSizeOptions={[10, 20, 50]}
          rowCount={totalCount}
          localeText={ptBR}
          isCellEditable={() => false}
          disableColumnFilter
          disableColumnSelector
          disableColumnSorting
          disableColumnMenu
          onCellClick={(params, event) => {
            if (params.field === "actions") {
              return;
            }
            openInvoiceDetails(params.row?.id);
          }}
          initialState={{
            sorting: {
              sortModel: [{ field: 'invoice_number', sort: 'desc' }],
            },
          }}
        />
      </Box>
      <Menu
        anchorEl={options}
        open={Boolean(options)}
        onClose={handleClose}
        slotProps={{
          list: {
            'aria-labelledby': 'basic-button',
          },
        }}
      >
        <MenuItem
          onClick={() => {
            handleClose();
            openInvoiceDetails(selectedRow?.id);
          }}
        >
          <ListItemIcon>
            <VisibilityRoundedIcon fontSize="medium" />
          </ListItemIcon>
          <Typography variant="body1">Ver detalhes</Typography>
        </MenuItem>
        <Tooltip
          title={!canCancelInvoice(selectedRow?.status) ? "O status dessa fatura não permite cancelamento " : ""}
          arrow
        >
          <span>
            <MenuItem
              onClick={() => {
                handleClose();
                setCancelModalOpen(true);
              }}
              disabled={!canCancelInvoice(selectedRow?.status)}
            >
              <ListItemIcon>
                <img src={InvoiceReceiptLongOffIcon} />
              </ListItemIcon>
              <Typography variant="body1">Cancelar fatura</Typography>
            </MenuItem>
          </span>
        </Tooltip>
      </Menu>
      <CancelModal
        open={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        invoiceId={selectedRow?.id}
        onErrorChange={handleCancelResult}
      />
      <ErrorAlertSnackbar
        open={errorSnackbarOpen}
        onClose={() => setErrorSnackbarOpen(false)}
        message="Não foi possível realizar a ação. Tente novamente."
        vertical="bottom"
        horizontal="center">
      </ErrorAlertSnackbar>
      <SuccessAlertSnackbar
        open={successSnackbarOpen}
        onClose={() => setSuccessSnackbarOpen(false)}
        message="Fatura cancelada com sucesso"
        vertical="bottom"
        horizontal="center" />
    </>
  );
};

export default InvoicesTable;
